import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { RotasIcon } from '../Layout/icons';
import { ROUTES } from '../../routes';
import { TimelineSection, TimelineTitle } from './DeliveryStatusTimeline.styles';
import { IntroText } from './DeliveryStatusUpdateSection.styles';
import {
    RouteTile,
    RouteTileArrow,
    RouteTileBody,
    RouteTileIcon,
    RouteTileMeta,
    RouteTileTitle,
    RoutesGrid,
} from './DeliveryAssociatedRoutesInfo.styles';

const SECTION_TITLE_ID = 'delivery-associated-routes-title';

function normalizeAssociatedRoutes(routes) {
    if (!Array.isArray(routes)) return [];
    return routes
        .filter((route) => route?.id != null && String(route.id).trim() !== '')
        .map((route) => ({
            id: route.id,
            pointCount: Array.isArray(route.points) ? route.points.length : 0,
        }));
}

function formatPointMeta(count) {
    if (count <= 0) return 'Nenhum ponto vinculado nesta entrega';
    if (count === 1) return '1 ponto desta entrega';
    return `${count} pontos desta entrega`;
}

/**
 * Seção com rotas associadas à entrega (empresa de transporte / RH interno).
 *
 * @param {{ routes?: Array<{ id?: number, points?: number[] }> }} props
 */
export default function DeliveryAssociatedRoutesInfo({ routes = [] }) {
    const navigate = useNavigate();
    const associatedRoutes = useMemo(() => normalizeAssociatedRoutes(routes), [routes]);

    if (associatedRoutes.length === 0) return null;

    const sectionTitle = associatedRoutes.length === 1 ? 'Rota associada' : 'Rotas associadas';
    const introText =
        associatedRoutes.length === 1
            ? 'Esta entrega consta em um roteiro. Abra o detalhe da rota para visualizar os pontos vinculados.'
            : 'Esta entrega consta em mais de um roteiro. Abra o detalhe de cada rota para visualizar os pontos vinculados.';

    const handleOpenRoute = (routeId) => {
        navigate(`${ROUTES.ROTAS}/${encodeURIComponent(String(routeId))}`);
    };

    return (
        <TimelineSection role="navigation" aria-labelledby={SECTION_TITLE_ID}>
            <TimelineTitle id={SECTION_TITLE_ID}>{sectionTitle}</TimelineTitle>
            <IntroText>{introText}</IntroText>

            <RoutesGrid>
                {associatedRoutes.map((route) => (
                    <RouteTile
                        key={route.id}
                        type="button"
                        onClick={() => handleOpenRoute(route.id)}
                        aria-label={`Abrir rota ${route.id}, ${formatPointMeta(route.pointCount)}`}
                    >
                        <RouteTileIcon aria-hidden>
                            <RotasIcon />
                        </RouteTileIcon>
                        <RouteTileBody>
                            <RouteTileTitle>Rota #{route.id}</RouteTileTitle>
                            <RouteTileMeta>{formatPointMeta(route.pointCount)}</RouteTileMeta>
                        </RouteTileBody>
                        <RouteTileArrow aria-hidden>→</RouteTileArrow>
                    </RouteTile>
                ))}
            </RoutesGrid>
        </TimelineSection>
    );
}
